import { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardProfilesService } from '../services/dashboardService';
import { getWidgetById } from '../widgets/registry';
import type { DashboardProfile, WidgetLayoutItem } from '../widgets/types';

let keyCounter = 0;
const nextKey = (widgetId: string) => `${widgetId}-${++keyCounter}`;

function collides(a: WidgetLayoutItem, b: WidgetLayoutItem): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function compactVertical(widgets: WidgetLayoutItem[]): WidgetLayoutItem[] {
  const sorted = [...widgets].sort((a, b) => a.y - b.y || a.x - b.x);
  const placed: WidgetLayoutItem[] = [];
  const yByKey = new Map<string, number>();
  for (const widget of sorted) {
    let y = 0;
    while (placed.some((other) => collides(other, { ...widget, y }))) y += 1;
    placed.push({ ...widget, y });
    yByKey.set(widget.i, y);
  }
  return widgets.map((widget) => ({ ...widget, y: yByKey.get(widget.i) ?? widget.y }));
}

function layoutsEqual(a: WidgetLayoutItem[], b: WidgetLayoutItem[]): boolean {
  if (a.length !== b.length) return false;
  for (let idx = 0; idx < a.length; idx++) {
    const ai = a[idx], bi = b[idx];
    if (ai.i !== bi.i || ai.x !== bi.x || ai.y !== bi.y || ai.w !== bi.w || ai.h !== bi.h) {
      return false;
    }
  }
  return true;
}

export function useDashboardProfiles() {
  const [profiles, setProfiles] = useState<DashboardProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<WidgetLayoutItem[] | null>(null);

  const activeProfile = profiles.find((p) => p.is_active) || profiles[0] || null;

  const loadProfiles = useCallback(async () => {
    try {
      const data = await dashboardProfilesService.list();
      setProfiles(data);
      const active = data.find((p) => p.is_active) || data[0];
      if (active) lastSavedRef.current = active.widgets;
    } catch {
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const debouncedSave = useCallback(
    (profileId: number, widgets: WidgetLayoutItem[]) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        try {
          await dashboardProfilesService.update(profileId, { widgets });
          lastSavedRef.current = widgets;
        } catch {
          // local state is source of truth during editing
        }
      }, 800);
    },
    [],
  );

  const activateProfile = useCallback(
    async (profileId: number) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      try {
        await dashboardProfilesService.activate(profileId);
        setProfiles((prev) => {
          const updated = prev.map((p) => ({ ...p, is_active: p.id === profileId }));
          const active = updated.find((p) => p.is_active);
          if (active) lastSavedRef.current = active.widgets;
          return updated;
        });
      } catch {
        // noop
      }
    },
    [],
  );

  const createProfile = useCallback(
    async (name: string, widgets?: WidgetLayoutItem[]) => {
      try {
        const created = await dashboardProfilesService.create(name, widgets);
        setProfiles((prev) => [...prev, created]);
        return created;
      } catch {
        return null;
      }
    },
    [],
  );

  const deleteProfile = useCallback(
    async (profileId: number) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      try {
        await dashboardProfilesService.delete(profileId);
        setProfiles((prev) => {
          const remaining = prev.filter((p) => p.id !== profileId);
          if (remaining.length > 0 && !remaining.some((p) => p.is_active)) {
            dashboardProfilesService.activate(remaining[0].id).catch(() => {});
            remaining[0] = { ...remaining[0], is_active: true };
          }
          return remaining;
        });
      } catch {
        // noop
      }
    },
    [],
  );

  const renameProfile = useCallback(
    async (profileId: number, name: string) => {
      try {
        await dashboardProfilesService.update(profileId, { name });
        setProfiles((prev) =>
          prev.map((p) => (p.id === profileId ? { ...p, name } : p)),
        );
      } catch {
        // noop
      }
    },
    [],
  );

  const updateLayout = useCallback(
    (newWidgets: WidgetLayoutItem[]) => {
      if (!activeProfile) return;
      if (!isEditMode) return;
      if (lastSavedRef.current && layoutsEqual(newWidgets, lastSavedRef.current)) return;

      setProfiles((prev) =>
        prev.map((p) =>
          p.id === activeProfile.id ? { ...p, widgets: newWidgets } : p,
        ),
      );
      debouncedSave(activeProfile.id, newWidgets);
    },
    [activeProfile, isEditMode, debouncedSave],
  );

  const addWidget = useCallback(
    (widgetId: string) => {
      if (!activeProfile) return;
      const def = getWidgetById(widgetId);
      if (!def) return;

      const maxY = activeProfile.widgets.reduce(
        (max, w) => Math.max(max, w.y + w.h),
        0,
      );

      const newWidget: WidgetLayoutItem = {
        widget_id: widgetId,
        i: nextKey(widgetId),
        x: 0,
        y: maxY,
        w: def.defaultW,
        h: def.defaultH,
      };
      const updated = [...activeProfile.widgets, newWidget];
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === activeProfile.id ? { ...p, widgets: updated } : p,
        ),
      );
      debouncedSave(activeProfile.id, updated);
    },
    [activeProfile, debouncedSave],
  );

  const setWidgetHeight = useCallback(
    (key: string, rows: number) => {
      if (!activeProfile) return;
      const current = activeProfile.widgets.find((widget) => widget.i === key);
      if (!current) return;
      const def = getWidgetById(current.widget_id);
      const h = Math.min(
        def?.constraints.maxH ?? rows,
        Math.max(def?.constraints.minH ?? 2, rows),
      );
      if (current.h === h) return;
      const updated = compactVertical(
        activeProfile.widgets.map((widget) => (widget.i === key ? { ...widget, h } : widget)),
      );
      if (lastSavedRef.current && layoutsEqual(updated, lastSavedRef.current)) return;
      setProfiles((prev) =>
        prev.map((profile) =>
          profile.id === activeProfile.id ? { ...profile, widgets: updated } : profile,
        ),
      );
      debouncedSave(activeProfile.id, updated);
    },
    [activeProfile, debouncedSave],
  );

  const removeWidget = useCallback(
    (key: string) => {
      if (!activeProfile) return;
      const updated = activeProfile.widgets.filter((w) => w.i !== key);
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === activeProfile.id ? { ...p, widgets: updated } : p,
        ),
      );
      debouncedSave(activeProfile.id, updated);
    },
    [activeProfile, debouncedSave],
  );

  return {
    profiles,
    activeProfile,
    loading,
    isEditMode,
    setIsEditMode,
    loadProfiles,
    activateProfile,
    createProfile,
    deleteProfile,
    renameProfile,
    updateLayout,
    addWidget,
    removeWidget,
    setWidgetHeight,
  };
}
